import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiFileText, FiUploadCloud, FiZap, FiDownload, FiCheckCircle, FiAlertCircle, FiInfo, FiScissors, FiShield, FiLoader } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { usePdfActions } from "../../hooks/usePdfActions";
import { useNotification } from "../../context/NotificationContext";

function PdfUploader() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { compressPdf } = usePdfActions();
  const { showNotification } = useNotification();
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/signin");
    }
  }, [isAuthenticated, navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setOriginalSize(selectedFile.size);
      setCompressedUrl(null);
      setCompressedSize(null);
    }
  };

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;

    setCompressedSize(null);
    setCompressedUrl(null);
    setProgress(0);

    const formData = new FormData();
    formData.append("pdf", file);

    setLoading(true);
    
    // Simulate progress
    const interval = setInterval(() => {
      setProgress(prev => (prev < 90 ? prev + 10 : prev));
    }, 200);

    try {
      const blob: Blob = await compressPdf(formData);
      clearInterval(interval);
      setProgress(100);
      setCompressedSize(blob.size);
      const url: string = window.URL.createObjectURL(blob);
      setCompressedUrl(url);
      showNotification("PDF compressed successfully!", "success");
    } catch (err) {
      clearInterval(interval);
      showNotification("Failed to compress PDF. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  function handleDownload() {
    if (compressedUrl) {
      const a = document.createElement("a");
      a.href = compressedUrl;
      a.download = `compressed_${file?.name || "file.pdf"}`;
      a.click();
    }
  }

  function formatBytes(bytes: number | null) {
    if (bytes === null) return "-";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  return (
    <div className="min-vh-100 hero-bg py-5 mt-5">
      <div className="blob animate-morph bg-secondary" style={{ top: '-10%', left: '10%', width: '550px', height: '550px', opacity: 0.1 }}></div>
      <div className="blob animate-morph bg-primary" style={{ bottom: '-10%', right: '10%', width: '600px', height: '600px', opacity: 0.1, animationDelay: '-4s' }}></div>

      <div className="container py-5 animate-fade-in-up z-10 position-relative">
        <div className="row justify-content-center">
          <div className="col-lg-9">
             <div className="card-premium overflow-hidden border-0 shadow-2xl glass">
                <div className="card-body p-0">
                    <div className="row g-0">
                      <div className="col-lg-4 bg-dark p-4 p-md-5 d-flex flex-column justify-content-between text-white">
                         <div>
                            <div className="bg-primary-gradient d-inline-flex p-3 rounded-4 mb-4 shadow-glow">
                               <FiScissors size={32} />
                            </div>
                            <h2 className="display-6 fw-900 mb-3" style={{ letterSpacing: '-1px' }}>PDF Shrink</h2>
                            <p className="opacity-50 fw-medium small mb-4 mb-lg-5">Advanced document size reduction using vector optimization technology.</p>
                            
                            <div className="d-flex flex-column gap-3 mb-4 mb-lg-0">
                               <div className="d-flex align-items-center gap-3 glass p-3 rounded-4 border-white border-opacity-10">
                                  <FiZap className="text-warning" />
                                  <span className="small fw-800">Fast Processing</span>
                               </div>
                               <div className="d-flex align-items-center gap-3 glass p-3 rounded-4 border-white border-opacity-10">
                                  <FiShield className="text-success" />
                                  <span className="small fw-800">Secure Storage</span>
                               </div>
                            </div>
                         </div>
                         <div className="mt-4 mt-lg-5 p-4 glass rounded-5 border-white border-opacity-5">
                            <div className="text-center">
                               <div className="fw-900 fs-2 mb-1">100%</div>
                               <div className="small text-muted fw-bold uppercase">Privacy Guaranteed</div>
                            </div>
                         </div>
                      </div>

                      <div className="col-lg-8 p-4 p-md-5 bg-white">
                        <div className="mb-5">
                           <h4 className="fw-900 text-dark mb-2">Upload Document</h4>
                           <p className="text-muted fw-medium">Drag and drop your PDF file to begin neural optimization.</p>
                        </div>


                        <form onSubmit={handleUpload}>
                          <div
                            className={`border-3 border-dashed rounded-5 p-4 p-md-5 text-center mb-4 transition-all cursor-pointer position-relative group ${
                              file ? "border-primary bg-primary bg-opacity-5" : "border-secondary-subtle bg-light"
                            }`}
                            onClick={() => document.getElementById("pdf-upload")?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary') }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary') }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const droppedFile = e.dataTransfer.files?.[0];
                              if (droppedFile?.type === "application/pdf") {
                                setFile(droppedFile);
                                setOriginalSize(droppedFile.size);
                              }
                            }}
                            style={{ minHeight: '250px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <div className={`mb-4 transition-all duration-500 ${file ? 'scale-110' : 'group-hover:scale-110'}`}>
                               <FiFileText size={80} className={file ? "text-primary" : "text-muted opacity-20"} />
                            </div>
                            <h5 className="fw-900 text-dark mb-1">{file ? file.name : "Select PDF Document"}</h5>
                            <p className="text-muted small fw-bold">{file ? formatBytes(file.size) : "Standard PDF files only"}</p>
                            <input
                              id="pdf-upload"
                              type="file"
                              accept=".pdf"
                              className="d-none"
                              onChange={handleFileChange}
                            />
                          </div>

                          {loading && (
                            <div className="mb-5 animate-fade-in-up">
                              <div className="d-flex justify-content-between mb-2">
                                <span className="fw-900 small text-primary uppercase tracking-wider">Shrinking document...</span>
                                <span className="fw-900 small">{progress}%</span>
                              </div>
                              <div className="progress bg-light rounded-pill overflow-hidden" style={{ height: "12px" }}>
                                <div 
                                   className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                                   style={{ width: `${progress}%`, boxShadow: '0 0 20px var(--primary-glow)' }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {compressedUrl && !loading && (
                            <div className="glass p-4 rounded-4 mb-4 border-0 shadow-sm animate-fade-in-up">
                               <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="small text-muted fw-bold">Optimized Size</span>
                                  <span className="small fw-900 text-success">{formatBytes(compressedSize)}</span>
                               </div>
                               <div className="d-flex justify-content-between align-items-center">
                                  <span className="small text-muted fw-bold">Savings</span>
                                  <span className="badge bg-success-subtle text-success rounded-pill px-3 fw-900">
                                     -{Math.round((1 - (compressedSize || 0) / (originalSize || 1)) * 100)}%
                                  </span>
                               </div>
                            </div>
                          )}

                          <div className="d-grid gap-3">
                             {!compressedUrl ? (
                               <button
                                 type="submit"
                                 className="btn-premium py-4 fs-4 d-flex align-items-center justify-content-center gap-3 shadow-2xl"
                                 disabled={loading || !file}
                               >
                                 {loading ? <FiLoader className="animate-spin" /> : <FiZap />}
                                 {loading ? "PROCESSING..." : "OPTIMIZE DOCUMENT"}
                               </button>
                             ) : (
                               <button
                                 type="button"
                                 onClick={handleDownload}
                                 className="btn-premium py-4 fs-4 d-flex align-items-center justify-content-center gap-3 shadow-2xl"
                                 style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                               >
                                 <FiDownload /> DOWNLOAD PDF
                               </button>
                             )}
                          </div>
                        </form>
                      </div>
                   </div>
                </div>
             </div>

             <div className="mt-5 d-flex justify-content-between align-items-center opacity-50 px-4">
                <div className="d-flex align-items-center gap-2">
                   <FiShield size={18} />
                   <span className="small fw-800">ENCRYPTED END-TO-END</span>
                </div>
                <div className="small fw-900">VER: 3.1.2</div>
             </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .uppercase { text-transform: uppercase; }
      `}</style>
    </div>
  );
}

export default PdfUploader;